/**
 * Client-side session token storage for the account / dashboard surface.
 *
 * The API is cross-origin and cookie-free, so the session is an opaque
 * bearer token we persist in localStorage and send in the `authorization`
 * header (see `fetchWithSession` in src/api/client.ts). This mirrors how
 * the report bearer token lives in the URL — the token IS the credential.
 *
 * This is account-level identity, distinct from the per-report token gated
 * by `?report=` (and from the per-report structures persistence in
 * structuresPersistence.ts). One browser = at most one signed-in account.
 */

const SESSION_KEY = "lotscanner-session";

/** Read the stored session token, or null when signed out. */
export function getSessionToken(): string | null {
  try {
    const value = window.localStorage.getItem(SESSION_KEY);
    return value && value.length > 0 ? value : null;
  } catch {
    // Private-mode / disabled storage: treat as signed out.
    return null;
  }
}

/** Persist a session token after a successful magic-link verify. */
export function setSessionToken(token: string): void {
  try {
    window.localStorage.setItem(SESSION_KEY, token);
  } catch {
    // Non-fatal: the user simply won't stay signed in across reloads.
  }
}

/** Clear the session token (sign out). */
export function clearSessionToken(): void {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // No-op.
  }
}

/** Convenience boolean for nav affordances. */
export function hasSession(): boolean {
  return getSessionToken() != null;
}
